import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { restaurantAPI } from '../services/api';

// Flutter App Theme Colors (exact match)
const theme = {
  primaryColor: '#FF6961',
  primaryLight: '#FF8A84',
  primaryDark: '#E6524A',
  backgroundColor: '#F8FAFC',
  surfaceColor: '#FFFFFF',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  successColor: '#22C55E',
  warningColor: '#F59E0B',
  errorColor: '#EF4444',
};

function RestaurantSidebar({ isCollapsed, onToggle, isMobile, onClose, orderCount = 0 }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [restaurant, setRestaurant] = useState(null);

  useEffect(() => {
    fetchRestaurant();
  }, []);

  const fetchRestaurant = async () => {
    try {
      const response = await restaurantAPI.getMyRestaurant();
      setRestaurant(response.data.data.restaurant);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      window.location.href = '/login';
    }
  };

  const isActive = (path) => {
    if (path === '/restaurant/dashboard') {
      return location.pathname === path || location.pathname === '/restaurant';
    }
    return location.pathname.startsWith(path);
  };

  // Navigation sections matching Flutter profile_screen menu order
  const navSections = [
    {
      title: 'Main',
      items: [
        { path: '/restaurant/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', label: 'Home' },
        { path: '/restaurant/orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', label: 'Orders', badge: orderCount },
        { path: '/restaurant/menu', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', label: 'Menu' },
        { path: '/restaurant/profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', label: 'Profile' },
      ]
    },
    {
      title: 'Manage',
      items: [
        { path: '/restaurant/reviews', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z', label: 'Reviews' },
        { path: '/restaurant/coupons', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z', label: 'Coupons' },
        { path: '/restaurant/opening-hours', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Opening Hours' },
        { path: '/restaurant/earnings', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Earnings' },
        { path: '/restaurant/invoices', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', label: 'Invoices' },
        { path: '/restaurant/sales-report', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', label: 'Sales Report' },
      ]
    },
    {
      title: 'Subscription',
      items: [
        { path: '/restaurant/subscription-info', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', label: 'Subscription' },
        { path: '/restaurant/addon-subscriptions', icon: 'M13 10V3L4 14h7v7l9-11h-7z', label: 'Premium Add-ons' },
      ]
    },
    {
      title: 'Support',
      items: [
        { path: '/restaurant/notification-settings', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9', label: 'Notifications' },
        { path: '/restaurant/printer-settings', icon: 'M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z', label: 'Printer Settings' },
        { path: '/restaurant/help-support', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Help & Support' },
        { path: '/restaurant/about', icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', label: 'About' },
      ]
    }
  ];

  const SvgIcon = ({ path, size = 20 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={path} />
    </svg>
  );

  const getInitial = () => {
    return restaurant?.name?.charAt(0)?.toUpperCase() || 'R';
  };

  // Responsive width values
  const sidebarWidth = isCollapsed && !isMobile
    ? 'clamp(60px, 8vw, 72px)'
    : isMobile
      ? 'min(280px, 85vw)'
      : 'clamp(220px, 20vw, 260px)';

  return (
    <aside style={{
      width: sidebarWidth,
      height: '100vh',
      background: `linear-gradient(180deg, ${theme.primaryColor} 0%, ${theme.primaryDark} 100%)`,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      boxShadow: '4px 0 24px rgba(255, 105, 97, 0.2)',
      overflowX: 'hidden',
    }}>
      {/* Close button for mobile */}
      {isMobile && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            zIndex: 10,
          }}
        >
          <SvgIcon path="M6 18L18 6M6 6l12 12" size={18} />
        </button>
      )}

      {/* Header - Restaurant Info */}
      <div style={{
        padding: isCollapsed && !isMobile ? '20px 12px' : '24px 20px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
        }}>
          {/* Restaurant Avatar - White box like Flutter */}
          <div style={{
            width: isCollapsed && !isMobile ? 'clamp(40px, 5vw, 48px)' : 'clamp(48px, 6vw, 56px)',
            height: isCollapsed && !isMobile ? 'clamp(40px, 5vw, 48px)' : 'clamp(48px, 6vw, 56px)',
            borderRadius: 'clamp(12px, 2vw, 16px)',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: isCollapsed && !isMobile ? 'clamp(18px, 3vw, 24px)' : 'clamp(22px, 3.5vw, 28px)',
              fontWeight: '700',
              color: theme.primaryColor,
            }}>
              {getInitial()}
            </span>
          </div>

          {/* Restaurant Name & Rating */}
          {(!isCollapsed || isMobile) && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{
                color: 'white',
                fontSize: '15px',
                fontWeight: '600',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {restaurant?.name || 'Restaurant'}
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={theme.warningColor}>
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px' }}>
                  {restaurant?.rating?.toFixed(1) || '0.0'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '12px 0',
      }}>
        {navSections.map((section) => (
          <div key={section.title} style={{ marginBottom: '8px' }}>
            {(!isCollapsed || isMobile) && (
              <div style={{
                padding: '8px 20px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '11px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}>
                {section.title}
              </div>
            )}
            {section.items.map((item) => {
              const active = isActive(item.path);
              const hovered = hoveredItem === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={isMobile ? onClose : undefined}
                  onMouseEnter={() => setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isCollapsed && !isMobile ? '12px' : '12px 20px',
                    margin: isCollapsed && !isMobile ? '4px 8px' : '2px 12px',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    color: 'white',
                    background: active
                      ? 'rgba(255, 255, 255, 0.25)'
                      : hovered
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
                    position: 'relative',
                  }}
                  title={isCollapsed && !isMobile ? item.label : ''}
                >
                  <span style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '22px',
                    height: '22px',
                    flexShrink: 0,
                    position: 'relative',
                  }}>
                    <SvgIcon path={item.icon} size={20} />
                    {/* Badge for orders */}
                    {item.badge > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-8px',
                        background: theme.errorColor,
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: '600',
                        padding: '2px 5px',
                        borderRadius: '10px',
                        minWidth: '16px',
                        textAlign: 'center',
                      }}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </span>
                  {(!isCollapsed || isMobile) && (
                    <span style={{
                      fontSize: '14px',
                      fontWeight: active ? '600' : '500',
                      whiteSpace: 'nowrap',
                      opacity: active ? 1 : 0.9,
                    }}>
                      {item.label}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer - Logout */}
      <div style={{
        padding: isCollapsed && !isMobile ? '16px 8px' : '16px',
        borderTop: '1px solid rgba(255, 255, 255, 0.15)',
      }}>
        <button
          onClick={handleLogout}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          style={{
            width: '100%',
            padding: '12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isCollapsed && !isMobile ? 'center' : 'flex-start',
            gap: '12px',
            transition: 'all 0.2s ease',
          }}
        >
          <SvgIcon path="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" size={20} />
          {(!isCollapsed || isMobile) && <span>Logout</span>}
        </button>
      </div>

      {/* Collapse Toggle - Desktop only */}
      {!isMobile && (
        <button
          onClick={onToggle}
          style={{
            position: 'absolute',
            top: '50%',
            right: '-12px',
            transform: 'translateY(-50%)',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'white',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.primaryColor,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'transform 0.2s ease',
            zIndex: 10,
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(-50%) scale(1)'}
        >
          <SvgIcon path={isCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} size={12} />
        </button>
      )}
    </aside>
  );
}

export default RestaurantSidebar;
